import { MessageDirection, MessageResponse, MessageType } from "client/openapi";
import { answeredEmailPeople, EmailRecipient } from "./EmailToLine";

/** The key prefix of a sender who matches no student, parent or tutor. */
const UNKNOWN_SENDER = "noLinkToPeopleInOrg";

/**
 * A message's row in the Communication view: the server's row_key (one rule
 * for the view and the counts).
 */
export function rowKey(message: MessageResponse): string {
  return message.row_key || `single-${message.id}`;
}

/**
 * Whether a row is a sender who matches no one (one row per address), or
 * staff's own email to no one on file (one row per thread): Unknown.
 */
export function isUnknownSender(row: string): boolean {
  return row.startsWith(`${UNKNOWN_SENDER}:`);
}

export function isInbound(message: MessageResponse): boolean {
  return message.direction === MessageDirection.INBOUND;
}

/** A row's newest message from the other side, if any (messages oldest first). */
export function newestInbound(
  messages: MessageResponse[]
): MessageResponse | undefined {
  return [...messages].reverse().find(isInbound);
}

/** A row's channel: text or email, as its newest inbound message came. */
export function rowChannel(
  messages: MessageResponse[]
): "sms" | "email" | "other" {
  const message = newestInbound(messages) ?? messages[messages.length - 1];
  return message?.sms ? "sms" : message?.email ? "email" : "other";
}

/**
 * Email from a sender who matches no one: viewable under Unknown, never
 * unread and never counted, so it shows no read controls.
 */
export function isQuiet(row: string, messages: MessageResponse[]): boolean {
  return isUnknownSender(row) && rowChannel(messages) === "email";
}

/** The row's inbound messages this admin hasn't read. */
export function unreadInbound(messages: MessageResponse[]): MessageResponse[] {
  return messages.filter((m) => isInbound(m) && !m.seen);
}

/**
 * Claude's draft reply to the row's newest received message, and that
 * message, until someone on staff replies after it.
 */
export function suggestionFor(
  messages: MessageResponse[]
): { message: MessageResponse; text: string } | undefined {
  const received = newestInbound(messages);
  if (!received?.suggested_reply) return undefined;
  const answered = messages.some(
    (m) =>
      m.direction === MessageDirection.OUTBOUND &&
      m.m_type === MessageType.DIRECT &&
      m.created_at > received.created_at
  );
  return answered
    ? undefined
    : { message: received, text: received.suggested_reply };
}

/**
 * The sender's address of an unknown row (its phone number or email); none
 * for a row of staff's own email alone, which keeps no address.
 */
export function unknownAddress(messages: MessageResponse[]): string {
  const message = newestInbound(messages);
  return message?.sender_phone || message?.sender_email || "";
}

/** Messages grouped into rows, newest row first, each row oldest first. */
export function groupRows(
  messages: MessageResponse[]
): [string, MessageResponse[]][] {
  const rows = new Map<string, MessageResponse[]>();
  const newest = (msgs: MessageResponse[]) =>
    Math.max(...msgs.map((m) => new Date(m.created_at).getTime()));
  for (const message of messages) {
    const row = rowKey(message);
    rows.set(row, [...(rows.get(row) ?? []), message]);
  }
  return Array.from(rows.entries())
    .map(
      ([row, msgs]) =>
        [
          row,
          [...msgs].sort(
            (a, b) =>
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime()
          ),
        ] as [string, MessageResponse[]]
    )
    .sort(([, a], [, b]) => newest(b) - newest(a));
}

/** An email (not a text): the messages Reply goes on. */
export function isEmail(message: MessageResponse): boolean {
  return !!message.email && !message.sms;
}

/** The email a reply answers unless another is picked: the row's newest. */
export function newestEmail(
  messages: MessageResponse[]
): MessageResponse | undefined {
  return [...messages].reverse().find(isEmail);
}

/** Everyone on a row's emails, listed first when adding someone to a reply. */
export function threadPeople(messages: MessageResponse[]): EmailRecipient[] {
  const people = new Map<string, EmailRecipient>();
  for (const message of messages.filter(isEmail)) {
    for (const person of answeredEmailPeople(message)) {
      people.set(`${person.role}:${person.id}`, person);
    }
  }
  return Array.from(people.values());
}

/**
 * The email a reply answers once it is picked (Reply clicked, or pinned as
 * soon as the admin starts on the reply), and the newest email then.
 * Chosen: picked with Reply (or Reply to it), so it shows as the email
 * being answered.
 */
export type Picked = {
  id: number;
  newestThen?: number;
  chosen?: boolean;
} | null;

export function pickEmail(
  messages: MessageResponse[],
  message: MessageResponse,
  chosen = false
): Picked {
  return { id: message.id, newestThen: newestEmail(messages)?.id, chosen };
}

/**
 * The email a reply answers (the picked one, else the newest: an untouched
 * reply follows new mail), and a newer email that came in since it was
 * picked, which the admin can choose to answer instead.
 */
export function answering(
  messages: MessageResponse[],
  picked: Picked
): { answered?: MessageResponse; newer?: MessageResponse } {
  const newest = newestEmail(messages);
  const answered =
    (picked && messages.find((m) => isEmail(m) && m.id === picked.id)) ||
    newest;
  const newer =
    picked &&
    newest &&
    newest.id !== picked.newestThen &&
    newest.id !== answered?.id
      ? newest
      : undefined;
  return { answered, newer };
}
